import React, { ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { openConfirmationDialog } from '@state/common/actions';

type Props = {
  /**
   * The feature, given whether to run it safely: without what the student saved, which is what an older build may
   * have left in a shape this one cannot read.
   */
  children: (safely: boolean) => ReactNode;
  /** The feature, as the student knows it: "Auto-plan". */
  name: string;
  /** Changes when the feature is opened or closed, so a failure is tried again next time rather than remembered. */
  resetKey: unknown;
  /** Puts the feature's own state back, such as closing its dialog. */
  onError: () => void;
  openConfirmationDialog: typeof openConfirmationDialog;
};

type State = { crashed: boolean; safely: boolean };

/**
 * Keeps one feature's crash to that feature. With nothing to catch it, an error while rendering unmounts the whole app
 * and leaves a blank page - which a preference saved by an older build did to Auto-plan on stage (2026-10-06).
 *
 * A crash is reported, then the feature is run again safely, so a student whose saved settings are at fault simply
 * gets the feature, on its defaults (Petr: a message does not help them). Only when that crashes too, which nothing
 * the student did can explain, does it close with a message, leaving the rest of the app working.
 */
export class FeatureErrorBoundary extends React.Component<Props, State> {
  state: State = { crashed: false, safely: false };

  static getDerivedStateFromError(): Partial<State> {
    return { crashed: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    Sentry.captureException(error, {
      tags: { feature: this.props.name, safely: String(this.state.safely) },
      contexts: { react: { componentStack: info.componentStack } }
    });
    if (!this.state.safely) {
      this.setState({ crashed: false, safely: true });
      return;
    }
    this.props.openConfirmationDialog({
      title: `${this.props.name} ran into a problem`,
      message: `Something went wrong on our side, and we've been told. Your calendar is fine. Please try ${this.props.name} again.`,
      hideCancel: true,
      action: () => {}
    });
    this.props.onError();
  }

  componentDidUpdate(previous: Props) {
    if (previous.resetKey !== this.props.resetKey && (this.state.crashed || this.state.safely)) {
      this.setState({ crashed: false, safely: false });
    }
  }

  render() {
    return this.state.crashed ? null : this.props.children(this.state.safely);
  }
}

const mapDispatchToProps = dispatch => bindActionCreators({ openConfirmationDialog }, dispatch);

export default connect(null, mapDispatchToProps)(FeatureErrorBoundary);
